import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Typography, Box, Chip, Alert,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';

/**
 * ActionDialog – Confirmation popup for Retain / Revoke actions.
 *
 * Props:
 *   open        – boolean
 *   action      – 'Retained' | 'Revoked'
 *   isBulk      – boolean (true = bulk action)
 *   selectedCount – number of selected users (for bulk display)
 *   userName    – single user's name (for single action display)
 *   onConfirm   – (comment: string) => void
 *   onCancel    – () => void
 *   loading     – boolean
 */
export default function ActionDialog({
  open,
  action,
  isBulk = false,
  selectedCount = 0,
  userName = '',
  onConfirm,
  onCancel,
  loading = false,
}) {
  const [comment, setComment] = useState('');

  // Reset comment when dialog opens
  useEffect(() => {
    if (open) setComment('');
  }, [open]);

  const isRetain = action === 'Retain';
  const actionLabel = isRetain ? 'Retain' : 'Revoke';
  const actionColor = isRetain ? '#1B8A2D' : '#C62828';
  const actionBg    = isRetain ? '#E8F5E9' : '#FFEBEE';
  const ActionIcon  = isRetain ? CheckCircleOutlineIcon : HighlightOffIcon;

  return (
    <Dialog
      open={open}
      onClose={!loading ? onCancel : undefined}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{
            width: 40, height: 40, borderRadius: 2, bgcolor: actionBg,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <ActionIcon sx={{ color: actionColor, fontSize: 22 }} />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, color: '#0A1929', fontSize: '1.05rem' }}>
              Confirm {actionLabel}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {isBulk
                ? `Bulk action on ${selectedCount} user(s)`
                : `Single user action`}
            </Typography>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        {/* Target info */}
        {isBulk ? (
          <Alert severity="info" sx={{ mb: 2.5, fontSize: '0.82rem' }}>
            You are about to <strong>{actionLabel.toLowerCase()}</strong> access for&nbsp;
            <Chip label={`${selectedCount} user(s)`} size="small"
              sx={{ fontWeight: 700, bgcolor: '#EBF5FF', color: '#0D47A1' }} />.
          </Alert>
        ) : (
          <Alert severity="info" sx={{ mb: 2.5, fontSize: '0.82rem' }}>
            You are about to <strong>{actionLabel.toLowerCase()}</strong> access for&nbsp;
            <strong>{userName}</strong>.
          </Alert>
        )}

        {/* Comment field */}
        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
          Comment {!isRetain && <span style={{ color: '#C62828' }}>*</span>}
        </Typography>
        <TextField
          placeholder={`Enter reason for ${actionLabel.toLowerCase()}…`}
          multiline
          minRows={3}
          maxRows={5}
          fullWidth
          size="small"
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          autoFocus
          sx={{
            '& .MuiOutlinedInput-root': {
              bgcolor: '#F8FAFC',
              '& fieldset': { borderColor: '#E3E8EF' },
            },
          }}
        />
        {!isRetain && !comment.trim() && (
          <Typography variant="caption" sx={{ color: '#C62828', mt: 0.5, display: 'block' }}>
            A comment is required for revocation.
          </Typography>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5, pt: 1, gap: 1 }}>
        <Button
          onClick={onCancel}
          disabled={loading}
          sx={{ color: '#5A6A7A', fontWeight: 600 }}
        >
          Cancel
        </Button>
        <Button
          onClick={() => onConfirm(comment.trim())}
          variant="contained"
          disabled={loading || (!isRetain && !comment.trim())}
          startIcon={<ActionIcon />}
          sx={{
            bgcolor: actionColor,
            '&:hover': { bgcolor: isRetain ? '#14662A' : '#8E0000' },
            px: 3,
            fontWeight: 600,
          }}
        >
          {loading ? 'Processing…' : actionLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
